/**
 * Role definitions = named capability bundles.
 *
 * MIRRORS `backend/app/core/rbac/roles.py`. Keep in sync when roles change.
 */

// Compli action-level capabilities (tiered RBAC -- Feature #24921). MIRRORS
// the same constants in backend/app/core/rbac/roles.py.
export const COMPLI_CLASSIFY_FINDING = 'tool:compli_classify_finding'
export const COMPLI_REVISE_DOCUMENT = 'tool:compli_revise_document'
export const COMPLI_MANAGE_STANDARDS = 'tool:compli_manage_standards'
// Held by compli_admin only: assign Compli tiers to other users. Deliberately in
// the admin: namespace (not tool:/surface:) so it is never an AI-tool gate and
// never self-requestable. MIRRORS COMPLI_ROLES_ADMIN in roles.py.
export const COMPLI_ROLES_ADMIN = 'admin:compli_roles'
// Read access to Qualio quality events, event templates, change controls and
// training records through the chat tools. Held by every Compli tier and by
// audrey. Not named tool:qualio_<x> because member/audrey/compli all hold the
// glob tool:qualio_*, which would match it. MIRRORS QMS_EVENTS_TRAINING in
// roles.py.
export const QMS_EVENTS_TRAINING = 'tool:qms_events_training'
// Marks a 3PH staff member (internal role). The chat prompt reads it to include
// 3PH-internal context, and the shared codebook read tools accept it in place
// of a specialist surface. Never self-requestable. MIRRORS INTERNAL_CONTEXT in
// roles.py.
export const INTERNAL_CONTEXT_CAP = 'context:3ph_internal'
// STRIDE study library + MDD chat tools (stride_*). Internal-only; never
// self-requestable. MIRRORS STRIDE_TOOLS in roles.py.
export const STRIDE_TOOLS_CAP = 'tool:stride'
// CMS Open Payments lookups — public data, held by bd and external. MIRRORS
// OPEN_PAYMENTS_TOOLS in roles.py.
export const OPEN_PAYMENTS_TOOLS_CAP = 'tool:open_payments'
// EDC mirror chat tools (edc_*). Deliberately held by NO role: only a platform
// admin passes, through the global '*'. MIRRORS EDC_TOOLS in roles.py.
export const EDC_TOOLS_CAP = 'tool:edc'

export const RBAC_ROLES: Readonly<Record<string, readonly string[]>> = {
  admin: ['*'],
  member: [
    'surface:concierge',
    'tool:survey_read',
    'tool:qualio_*',
  ],
  study_designer: [
    'surface:concierge',
    'surface:study-design',
    'surface:val',
    'surface:norma',
    'surface:testcases',
  ],
  bd: [
    'surface:concierge', 'surface:marq', 'tool:marq_*', 'tool:survey_read',
    OPEN_PAYMENTS_TOOLS_CAP,
  ],
  audrey: [
    'surface:concierge', 'surface:audrey', 'tool:audrey_*', 'tool:qualio_*',
    QMS_EVENTS_TRAINING,
  ],
  nora: ['surface:concierge', 'surface:nora', 'tool:nora_*'],
  // Legacy flat bundle -- superseded by the compli_* tiers below, see
  // Feature #24921; same capabilities as compli_basic, kept so no
  // currently-granted user loses access. Carries its own surface:concierge,
  // same as every other specialist role (bd/audrey/nora above).
  compli: ['surface:concierge', 'surface:compli', 'tool:qualio_*', QMS_EVENTS_TRAINING],
  compli_basic: ['surface:concierge', 'surface:compli', 'tool:qualio_*', QMS_EVENTS_TRAINING],
  compli_quality: [
    'surface:concierge', 'surface:compli', 'tool:qualio_*', QMS_EVENTS_TRAINING,
    COMPLI_CLASSIFY_FINDING, COMPLI_REVISE_DOCUMENT,
  ],
  compli_admin: [
    'surface:concierge', 'surface:compli', 'tool:qualio_*', QMS_EVENTS_TRAINING,
    COMPLI_CLASSIFY_FINDING, COMPLI_REVISE_DOCUMENT, COMPLI_MANAGE_STANDARDS,
    COMPLI_ROLES_ADMIN,
  ],
  // 3PH staff, layered on top of a user's other roles: internal chat context +
  // the STRIDE study library. Carries no surface of its own.
  internal: [INTERNAL_CONTEXT_CAP, STRIDE_TOOLS_CAP],
  // Non-3PH partner/beta accounts: Concierge with public data tools, standards
  // lookups and CMS Open Payments only.
  external: ['surface:concierge', OPEN_PAYMENTS_TOOLS_CAP],
}

/** Expand role names into the union of their capabilities. */
export function expandRoles(roleNames: readonly string[]): Set<string> {
  const caps = new Set<string>()
  for (const name of roleNames) {
    for (const cap of RBAC_ROLES[name] ?? []) {
      caps.add(cap)
    }
  }
  return caps
}
